/**
 * Sermon export helpers — emit either plain text, Markdown, or trigger
 * the browser's print dialog for "Save as PDF". Zero dependencies.
 */

const buildMarkdown = ({ topic, notes, outline, research }) => {
  const lines = [];
  lines.push(`# ${topic || 'Sermon'}`);
  lines.push('');

  if (notes) {
    lines.push('## Notes');
    lines.push('');
    lines.push(notes);
    lines.push('');
  }

  lines.push('## Outline');
  lines.push('');
  (outline || []).forEach((p, i) => {
    lines.push(`### ${i + 1}. ${p.title || ''}`);
    if (p.content) {
      lines.push('');
      lines.push(p.content);
    }
    lines.push('');
  });

  if (research) {
    lines.push('---');
    lines.push('## Biblical Research');
    lines.push('');

    if (research.key_verses?.length) {
      lines.push('### Key Verses');
      lines.push('');
      research.key_verses.forEach((v) => {
        lines.push(`**${v.reference}** _(${v.translation})_`);
        lines.push('');
        lines.push(`> ${v.text}`);
        if (v.relevance) {
          lines.push('');
          lines.push(`_Relevance:_ ${v.relevance}`);
        }
        lines.push('');
      });
    }

    if (research.concordance?.word_occurrences?.length) {
      lines.push('### Concordance');
      lines.push('');
      research.concordance.word_occurrences.forEach((w) => {
        const heb = w.hebrew_greek ? ` _(${w.hebrew_greek})_` : '';
        lines.push(`#### ${w.word.toUpperCase()}${heb}`);
        if (w.definition) lines.push(w.definition);
        if (w.key_references?.length) {
          lines.push('');
          lines.push(`_References:_ ${w.key_references.join(', ')}`);
        }
        lines.push('');
      });
    }

    if (research.biblical_dictionary?.length) {
      lines.push('### Biblical Dictionary');
      lines.push('');
      research.biblical_dictionary.forEach((e) => {
        lines.push(`#### ${e.term.toUpperCase()}`);
        if (e.definition) lines.push(e.definition);
        if (e.old_testament_usage) {
          lines.push('');
          lines.push(`**Old Testament:** ${e.old_testament_usage}`);
        }
        if (e.new_testament_usage) {
          lines.push(`**New Testament:** ${e.new_testament_usage}`);
        }
        if (e.theological_significance) {
          lines.push('');
          lines.push(`_Theological significance:_ ${e.theological_significance}`);
        }
        lines.push('');
      });
    }

    if (research.themes?.length) {
      lines.push('### Theological Themes');
      lines.push('');
      research.themes.forEach((t, i) => {
        const theme = t.theme || t;
        lines.push(`${i + 1}. **${theme}**`);
        if (t.explanation) lines.push(`   ${t.explanation}`);
        if (t.supporting_verses?.length) {
          lines.push(`   _Supporting verses:_ ${t.supporting_verses.join(', ')}`);
        }
      });
      lines.push('');
    }

    if (research.application?.length) {
      lines.push('### Application Points');
      lines.push('');
      research.application.forEach((p, i) => lines.push(`${i + 1}. ${p}`));
      lines.push('');
    }

    if (research.cross_references?.length) {
      lines.push('### Cross References');
      lines.push('');
      research.cross_references.forEach((r) => {
        const ref = r.reference || r;
        let line = `- **${ref}**`;
        if (r.connection) line += ` — ${r.connection}`;
        lines.push(line);
      });
      lines.push('');
    }
  }

  return lines.join('\n');
};

const downloadFile = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const exportSermonAsMarkdown = (data) => {
  const md = buildMarkdown(data);
  const safe = (data.topic || 'sermon').replace(/[^\w-]+/g, '_').slice(0, 60) || 'sermon';
  downloadFile(md, `${safe}.md`, 'text/markdown;charset=utf-8');
  return md;
};

// Lightweight Markdown → HTML for print preview (covers the subset our
// builder emits: # headings, **bold**, _italic_, `> blockquote`, `- list`,
// numbered lists, --- horizontal rule, paragraphs).
const escapeHtml = (s) => s.replace(/[&<>]/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;',
})[c]);

const inlineMd = (s) => escapeHtml(s)
  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  .replace(/(^|[^_])_([^_]+?)_/g, '$1<em>$2</em>');

const mdToHtml = (md) => {
  const out = [];
  let inList = false;
  let listType = null;   // 'ul' | 'ol'
  let inBlockquote = false;
  for (const raw of md.split(/\r?\n/)) {
    const line = raw;
    if (/^---\s*$/.test(line)) {
      if (inList) { out.push(`</${listType}>`); inList = false; listType = null; }
      out.push('<hr/>'); continue;
    }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      if (inList) { out.push(`</${listType}>`); inList = false; listType = null; }
      const n = h[1].length;
      out.push(`<h${n}>${inlineMd(h[2])}</h${n}>`);
      continue;
    }
    const ul = line.match(/^[-*]\s+(.*)$/);
    const ol = line.match(/^\d+\.\s+(.*)$/);
    if (ul || ol) {
      const want = ul ? 'ul' : 'ol';
      if (!inList || listType !== want) {
        if (inList) out.push(`</${listType}>`);
        out.push(`<${want}>`);
        listType = want; inList = true;
      }
      out.push(`<li>${inlineMd((ul || ol)[1])}</li>`);
      continue;
    }
    if (inList) { out.push(`</${listType}>`); inList = false; listType = null; }
    const bq = line.match(/^>\s?(.*)$/);
    if (bq) {
      if (!inBlockquote) { out.push('<blockquote>'); inBlockquote = true; }
      out.push(`<p>${inlineMd(bq[1])}</p>`);
      continue;
    }
    if (inBlockquote) { out.push('</blockquote>'); inBlockquote = false; }
    if (line.trim() === '') { out.push(''); continue; }
    out.push(`<p>${inlineMd(line)}</p>`);
  }
  if (inList) out.push(`</${listType}>`);
  if (inBlockquote) out.push('</blockquote>');
  return out.join('\n');
};

export const exportSermonAsPdf = (data) => {
  const md = buildMarkdown(data);
  const html = mdToHtml(md);
  const title = (data.topic || 'Sermon').replace(/</g, '&lt;');
  const w = window.open('', '_blank');
  if (!w) {
    // Pop-up blocker — fall back to navigating in-place.
    const url = URL.createObjectURL(new Blob([htmlDoc(title, html)], { type: 'text/html' }));
    window.location.href = url;
    return;
  }
  w.document.open();
  w.document.write(htmlDoc(title, html));
  w.document.close();
  // Slight delay so fonts/layout settle before the print dialog opens.
  setTimeout(() => {
    w.focus();
    w.print();
  }, 300);
};

/**
 * Print a polished sermon manuscript with a dedicated cover page (sermon
 * title + date + series + preacher + optional subtitle), followed by the
 * full outline + research on subsequent pages. Uses the same print-to-PDF
 * pattern (window.open → @media print) so no new dependencies are required.
 *
 * `manuscriptInfo` shape:
 *   { date, series_name, series_position, preacher, subtitle }
 *   All fields are optional; missing ones are quietly omitted from the cover.
 */
export const exportSermonManuscriptPdf = (data, manuscriptInfo = {}) => {
  const md = buildMarkdown(data);
  const body = mdToHtml(md);
  const title = (data.topic || 'Sermon').replace(/</g, '&lt;');
  const cover = manuscriptCoverHtml(data, manuscriptInfo);
  const fullDoc = htmlManuscriptDoc(title, cover, body);

  const w = window.open('', '_blank');
  if (!w) {
    const url = URL.createObjectURL(new Blob([fullDoc], { type: 'text/html' }));
    window.location.href = url;
    return;
  }
  w.document.open();
  w.document.write(fullDoc);
  w.document.close();
  setTimeout(() => {
    w.focus();
    w.print();
  }, 350);
};

const fmtDate = (iso) => {
  if (!iso) return '';
  // Accept either YYYY-MM-DD or any value Date can parse. Format as "March 12, 2026".
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const manuscriptCoverHtml = (data, info) => {
  const safe = (s) => (s == null ? '' : String(s).replace(/[&<>]/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;',
  })[c]));
  const topic = safe(data.topic || 'Sermon');
  const subtitle = safe(info.subtitle || '');
  const series = info.series_name
    ? `${safe(info.series_name)}${info.series_position ? ` · #${safe(info.series_position)}` : ''}`
    : '';
  const date = fmtDate(info.date);
  const preacher = safe(info.preacher || '');
  return `
    <section class="cover">
      <div class="cover-frame">
        <p class="cover-eyebrow">Sermon Manuscript</p>
        <h1 class="cover-title">${topic}</h1>
        ${subtitle ? `<p class="cover-subtitle">${subtitle}</p>` : ''}
        <div class="cover-divider"></div>
        <dl class="cover-meta">
          ${series ? `<div><dt>Series</dt><dd>${series}</dd></div>` : ''}
          ${date ? `<div><dt>Date</dt><dd>${date}</dd></div>` : ''}
          ${preacher ? `<div><dt>Preacher</dt><dd>${preacher}</dd></div>` : ''}
        </dl>
      </div>
    </section>
  `;
};

const htmlManuscriptDoc = (title, cover, body) => `<!doctype html>
<html><head>
<meta charset="utf-8">
<title>${title} — Manuscript</title>
<style>
  @page { margin: 0.9in 1in; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Georgia, 'Times New Roman', serif;
    color: #1f2937;
    max-width: 6.5in;
    margin: 0 auto;
    line-height: 1.6;
    font-size: 12pt;
  }
  /* Cover page — full-page centered, with a hard page break after */
  .cover {
    min-height: 9in;
    display: flex;
    align-items: center;
    justify-content: center;
    page-break-after: always;
    break-after: page;
  }
  .cover-frame {
    text-align: center;
    padding: 1.2in 0.4in;
    border-top: 4px solid #4c1d95;
    border-bottom: 4px solid #4c1d95;
    width: 100%;
  }
  .cover-eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.3em;
    font-size: 9pt;
    color: #7c3aed;
    margin: 0 0 0.6em 0;
  }
  .cover-title {
    font-size: 32pt;
    font-weight: 700;
    color: #4c1d95;
    margin: 0 0 0.3em;
    line-height: 1.2;
    border: 0;
    padding: 0;
  }
  .cover-subtitle {
    font-size: 14pt;
    font-style: italic;
    color: #6b7280;
    margin: 0 0 1.4em;
  }
  .cover-divider {
    width: 60px;
    height: 2px;
    background: #c4b5fd;
    margin: 1.8em auto;
  }
  .cover-meta {
    display: flex;
    justify-content: center;
    gap: 1.6em;
    flex-wrap: wrap;
    margin: 0;
  }
  .cover-meta div {
    text-align: center;
    min-width: 1.3in;
  }
  .cover-meta dt {
    font-size: 8.5pt;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: #7c3aed;
    margin-bottom: 0.25em;
  }
  .cover-meta dd {
    font-size: 11pt;
    margin: 0;
    color: #1f2937;
  }
  /* Manuscript body — same style as the regular PDF export */
  .manuscript h1 { font-size: 22pt; margin: 0 0 0.4em; color: #4c1d95; border-bottom: 2px solid #c4b5fd; padding-bottom: 0.25em; }
  .manuscript h2 { font-size: 15pt; margin-top: 1.2em; color: #6d28d9; }
  .manuscript h3 { font-size: 12.5pt; margin-top: 1em; color: #7c3aed; }
  .manuscript h4 { font-size: 11.5pt; margin-top: 0.9em; color: #5b21b6; }
  .manuscript p, .manuscript li { margin: 0.4em 0; }
  .manuscript blockquote {
    margin: 0.6em 0;
    padding: 0.4em 0.9em;
    border-left: 3px solid #a78bfa;
    background: #faf5ff;
    color: #4c1d95;
    font-style: italic;
  }
  .manuscript hr { border: 0; border-top: 1px dashed #c4b5fd; margin: 1.2em 0; }
  .manuscript ul, .manuscript ol { padding-left: 1.4em; }
  .manuscript strong { color: #1f2937; }
  .manuscript em { color: #6d28d9; }
  @media print { a { color: inherit; text-decoration: none; } }
</style></head>
<body>
${cover}
<section class="manuscript">
${body}
</section>
</body></html>`;


const htmlDoc = (title, body) => `<!doctype html>
<html><head>
<meta charset="utf-8">
<title>${title}</title>
<style>
  @page { margin: 1in; }
  body {
    font-family: Georgia, 'Times New Roman', serif;
    color: #1f2937;
    max-width: 6.5in;
    margin: 0 auto;
    line-height: 1.55;
    font-size: 11.5pt;
  }
  h1 { font-size: 22pt; margin: 0 0 0.4em; color: #4c1d95; border-bottom: 2px solid #c4b5fd; padding-bottom: 0.25em; }
  h2 { font-size: 15pt; margin-top: 1.2em; color: #6d28d9; }
  h3 { font-size: 12.5pt; margin-top: 1em; color: #7c3aed; }
  h4 { font-size: 11.5pt; margin-top: 0.9em; color: #5b21b6; }
  p, li { margin: 0.4em 0; }
  blockquote {
    margin: 0.6em 0;
    padding: 0.4em 0.9em;
    border-left: 3px solid #a78bfa;
    background: #faf5ff;
    color: #4c1d95;
    font-style: italic;
  }
  hr { border: 0; border-top: 1px dashed #c4b5fd; margin: 1.2em 0; }
  ul, ol { padding-left: 1.4em; }
  strong { color: #1f2937; }
  em { color: #6d28d9; }
  @media print { a { color: inherit; text-decoration: none; } }
</style></head>
<body>
${body}
</body></html>`;
